import {
  CardStyleInterpolators,
  StackScreenProps,
} from "@react-navigation/stack";
import {
  SaleRootParamList,
  SalesListFilterRootParamList,
} from "../../../../types";
import { useListFilterContext } from "../../../../utilities/list-filter/use-list-filter-context";
import {
  Button,
  ModalCloseButton,
  createStackHeaderNavigator,
} from "@towersystems/roam-common-native";
import { SalesListFilterScreen } from "../SalesListFilterScreen";
import { useTranslation } from "../../../../utilities/i18n";
import {
  FilterListRenderContextType,
  GenericFilterListScreen,
  useGenericFilterListScreen,
} from "../../../../components/GenericFilterListScreen";
import { SalesFilterValues } from "../../types";
import {
  SelectableProductsFilterContext,
  SelectableProductsFilterContextType,
} from "../../../../components/SelectableProductsFilterScreen/context";
import { SelectableProductsFilterScreen } from "../../../../components/SelectableProductsFilterScreen";
import {
  SelectableCustomersFilterContext,
  SelectableCustomersFilterContextType,
} from "../../../../components/SelectableCustomersFilterScreen/context";
import { SelectableCustomersFilterScreen } from "../../../../components/SelectableCustomersFilterScreen";

export interface SalesListFilterRootScreenProps
  extends StackScreenProps<SaleRootParamList, "SalesListFilterRootScreen"> {}

export const SalesListFilterRootScreen =
  ({}: SalesListFilterRootScreenProps) => {
    const listScreenContext = useGenericFilterListScreen();
    const filterContext = useListFilterContext<SalesFilterValues>();
    const { t } = useTranslation("sales");

    const RootNavigator =
      createStackHeaderNavigator<SalesListFilterRootParamList>();

    const selectableProductsContext: SelectableProductsFilterContextType = {
      selectedProductIds: filterContext.getFilterValues("productId") ?? [],
      onClearAllPress: () => filterContext.setFilterValue("productId", []),
      onProductSelect: (productId) => {
        const existingProductIds = filterContext.getFilterValues("productId");
        if (existingProductIds?.includes(productId)) {
          filterContext.setFilterValue(
            "productId",
            existingProductIds.filter((id) => id !== productId)
          );
          return;
        } else {
          filterContext.setFilterValue("productId", [
            ...(filterContext.getFilterValues("productId") ?? []),
            productId,
          ]);
        }
      },
    };

    const selectableCustomersContext: SelectableCustomersFilterContextType = {
      selectedCustomerIds: filterContext.getFilterValues("customerId") ?? [],
      onClearAllPress: () => filterContext.setFilterValue("customerId", []),
      onCustomerSelect: (customerId) => {
        const existingCustomerIds = filterContext.getFilterValues("customerId");
        if (existingCustomerIds?.includes(customerId)) {
          filterContext.setFilterValue(
            "customerId",
            existingCustomerIds.filter((id) => id !== customerId)
          );
          customerId;
          return;
        } else {
          filterContext.setFilterValue("customerId", [
            ...(filterContext.getFilterValues("customerId") ?? []),
            customerId,
          ]);
        }
      },
    };

    return (
      <FilterListRenderContextType.Provider value={listScreenContext}>
        <SelectableCustomersFilterContext.Provider
          value={selectableCustomersContext}
        >
          <SelectableProductsFilterContext.Provider
            value={selectableProductsContext}
          >
            <RootNavigator.Navigator
              screenOptions={{
                animationEnabled: true,
                cardStyleInterpolator: CardStyleInterpolators.forHorizontalIOS,
              }}
            >
              <RootNavigator.Group
                screenOptions={{
                  headerShown: true,
                  title: "",
                  headerLeft: () => <ModalCloseButton type="Close" />,
                  headerRight(props) {
                    return (
                      <Button
                        label={t("common:clear_filters")}
                        isAction
                        disabled={
                          filterContext.filterState.activeFiltersCount === 0
                        }
                        onPress={filterContext.clearAll}
                        size="compact"
                      />
                    );
                  },
                }}
              >
                <RootNavigator.Screen
                  name="SalesListFilterScreen"
                  component={SalesListFilterScreen}
                />
              </RootNavigator.Group>
              <RootNavigator.Group
                screenOptions={{
                  headerLeft: () => <ModalCloseButton type="Back" />,
                  title: "",
                }}
              >
                <RootNavigator.Screen
                  name="GenericFilterListScreen"
                  component={GenericFilterListScreen}
                />
                <RootNavigator.Screen
                  name="SelectableProductsFilterScreen"
                  component={SelectableProductsFilterScreen}
                />
                <RootNavigator.Screen
                  name="SelectableCustomersFilterScreen"
                  component={SelectableCustomersFilterScreen}
                />
              </RootNavigator.Group>
            </RootNavigator.Navigator>
          </SelectableProductsFilterContext.Provider>
        </SelectableCustomersFilterContext.Provider>
      </FilterListRenderContextType.Provider>
    );
  };
